import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { AuthService, AuthResponse } from '../../../../core/services/auth/auth.service';
import { AlertService } from '../../../../core/services/alert/alert.service';
import { SessionManagerService } from '../../../../core/services/session-manager/session-manager.service';
import { StoredSession } from '../../../../core/services/session-manager/session.model';

@Component({
  selector: 'app-relogin-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule],
  templateUrl: './relogin-modal.component.html',
  styleUrl: './relogin-modal.component.css',
})
export class ReloginModalComponent {
  @Input() session!: StoredSession;
  @Output() success = new EventEmitter<number>();
  @Output() cancel = new EventEmitter<void>();

  password = '';
  showMfa = false;
  challengeToken = '';
  mfaCode = '';
  rememberDevice = false;
  mfaError = '';
  loading = false;
  countdown = 0;
  countdownInterval: any = null;

  constructor(
    private authService: AuthService,
    private alertService: AlertService,
    private sessionManager: SessionManagerService
  ) {}

  get sessionIndex(): number {
    return this.sessionManager.getSessions().findIndex(s => s.email === this.session.email);
  }

  onSubmit(): void {
    if (!this.password || this.loading) return;

    this.loading = true;
    this.authService.login({ email: this.session.email, password: this.password }, 'add').subscribe({
      next: (response: AuthResponse) => {
        this.loading = false;
        if (this.authService.isMfaRequired(response)) {
          this.challengeToken = response.data!.challenge_token!;
          this.showMfa = true;
          this.mfaError = '';
          this.mfaCode = '';
          this.startCountdown();
        } else {
          this.handleSuccess(response);
        }
      },
      error: () => {
        this.loading = false;
        this.alertService.warning('swal_messages.login.login_failed');
      },
    });
  }

  onVerifyCode(): void {
    if (!this.mfaCode || this.mfaCode.length !== 6) {
      this.mfaError = 'swal_messages.mfa.mfa_code_invalid';
      return;
    }

    this.loading = true;
    this.authService.verifyMfa({
      challenge_token: this.challengeToken,
      code: this.mfaCode,
      remember_device: this.rememberDevice || undefined,
      device_label: this.rememberDevice ? navigator.userAgent.substring(0, 255) : undefined,
    }).subscribe({
      next: (response: AuthResponse) => {
        this.loading = false;
        this.handleSuccess(response);
      },
      error: () => {
        this.loading = false;
        this.mfaError = 'swal_messages.mfa.mfa_code_invalid';
      },
    });
  }

  onResendCode(): void {
    if (this.countdown > 0) return;
    this.loading = true;
    this.authService.resendMfa(this.challengeToken).subscribe({
      next: () => {
        this.loading = false;
        this.mfaCode = '';
        this.mfaError = '';
        this.startCountdown();
      },
      error: () => {
        this.loading = false;
        this.mfaError = 'swal_messages.mfa.mfa_resend_too_soon';
      },
    });
  }

  onCancel(): void {
    this.cancel.emit();
  }

  private handleSuccess(response: AuthResponse): void {
    const idx = this.sessionIndex;
    if (idx >= 0) {
      this.sessionManager.updateSession(idx, response.access_token!, response.device_token ?? null);
    }
    this.success.emit(idx);
  }

  private startCountdown(): void {
    this.clearCountdown();
    this.countdown = 60;
    this.countdownInterval = setInterval(() => {
      this.countdown--;
      if (this.countdown <= 0) this.clearCountdown();
    }, 1000);
  }

  private clearCountdown(): void {
    this.countdown = 0;
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
  }
}
